import React, { useState } from "react";
import { api } from "@/planner/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { RefreshCw, Link2 } from "lucide-react";
import SandstarLibraryAdd from "@/planner/components/SandstarLibraryAdd";

// What Sandstar says about this machine: which Sandstar machine it is, live stock, and the last
// 30 days of best sellers. Filled by "Sync stock & sales" (POST /sandstar/machines/sync).
export default function SandstarPanel({ machine, canEdit, onSynced }) {
  const info = machine.sandstar;
  const [choices, setChoices] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadChoices = async () => {
    setBusy(true);
    try { const { data } = await api.get("/sandstar/machines"); setChoices(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't reach Sandstar"); }
    finally { setBusy(false); }
  };
  const link = async (id) => {
    const m = (choices || []).find((c) => c.id === id);
    try {
      await api.put(`/sandstar/machines/link/${machine.id}`, { sandstar_machine_id: id, sandstar_machine_name: m?.name || "" });
      toast.success(id ? `Linked to ${m?.name}` : "Unlinked");
      onSynced && onSynced();
    } catch (e) { toast.error("Couldn't save the link"); }
  };
  const sync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sandstar/machines/sync");
      toast.success(`Synced ${data.synced.length} machine${data.synced.length === 1 ? "" : "s"} from Sandstar`);
      onSynced && onSynced();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sandstar sync failed"); }
    finally { setBusy(false); }
  };

  const top = (info?.sales?.items || []).slice(0, 10);
  return (
    <Card className="mt-6 p-4" data-testid="sandstar-panel">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-semibold">Sandstar</h2>
          <p className="text-sm text-muted-foreground">
            {machine.sandstar_machine_id
              ? <>Linked to <b>{machine.sandstar_machine_name || machine.sandstar_machine_id}</b>{info?.synced_at && <> · synced {new Date(info.synced_at).toLocaleString()}</>}</>
              : "Not linked to a Sandstar machine yet. A sync links it when the names clearly match, or pick it here."}
          </p>
        </div>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            {choices ? (
              <Select value={machine.sandstar_machine_id || "none"} onValueChange={(v) => link(v === "none" ? "" : v)}>
                <SelectTrigger className="h-9 w-56" data-testid="sandstar-link-select"><SelectValue placeholder="Pick the Sandstar machine" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Not linked</SelectItem>
                  {choices.map((c) => <SelectItem key={c.id} value={c.id}>{c.name || c.id}</SelectItem>)}
                </SelectContent>
              </Select>
            ) : (
              <Button variant="outline" size="sm" onClick={loadChoices} disabled={busy} data-testid="sandstar-link-button"><Link2 className="mr-2 h-4 w-4" />Link</Button>
            )}
            <SandstarLibraryAdd machine={machine} />
            <Button size="sm" onClick={sync} disabled={busy} data-testid="sandstar-machine-sync"><RefreshCw className={`mr-2 h-4 w-4 ${busy ? "animate-spin" : ""}`} />Sync stock & sales</Button>
          </div>
        )}
      </div>
      {info && (
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <p className="text-sm"><b>{info.units_in_machine}</b> items in the machine now. Shelf counts above are set from this.</p>
            {info.not_in_planogram?.length > 0 && (
              <div className="mt-2 text-sm">
                <p className="font-medium">In the machine but not on this planogram:</p>
                <ul className="mt-1 list-disc pl-5 text-muted-foreground">
                  {info.not_in_planogram.map((x) => <li key={x.upc}>{x.name} ({x.qty})</li>)}
                </ul>
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-medium">Best sellers, last {info.sales?.days || 30} days</p>
            <p className="text-xs text-muted-foreground">{info.sales?.orders || 0} orders · {info.sales?.units || 0} items · ${Number(info.sales?.revenue || 0).toFixed(2)}</p>
            {top.length ? (
              <ol className="mt-2 space-y-1 text-sm" data-testid="sandstar-best-sellers">
                {top.map((x, i) => (
                  <li key={(x.upc || x.name) + i} className="flex justify-between gap-2">
                    <span className="truncate">{i + 1}. {x.name}</span><span className="tabular-nums text-muted-foreground">{x.units}</span>
                  </li>
                ))}
              </ol>
            ) : <p className="mt-2 text-sm text-muted-foreground">No sales yet.</p>}
          </div>
        </div>
      )}
    </Card>
  );
}
